import { handleActions, createAction } from "redux-actions";
import config from "appConfig";
import { api, checkEmptyObject, getStatusString, formatDate, formatNumber, notification } from "../../../services";
import { AD_DUPLICATE_TAB_KEY, AdsSortOption, AdStatus } from "../../../models";
import { PriceChange, PriceChangeList, PriceChangeListParsed, PriceChangeParsed } from "../../../typings/interfaces";

const AD_GET = "AD_GET";
const AD_DUPLICATES_GET = "AD_DUPLICATES_GET";
const AD_FLAG_AS_DUPLICATE = "AD_FLAG_AS_DUPLICATE";
const AD_SEARCH = "AD_SEARCH";
const AD_CHANGE_STATUS = "AD_CHANGE_STATUS";
const AD_BLOCK = "AD_BLOCK";
const AD_LINK_GET = "AD_LINK_GET";
const AD_LINK_UPDATE = "AD_LINK_UPDATE";
const AD_18_OLD_TOGGLE = "AD_18_OLD_TOGGLE";
const AD_REDUCED_PRICE_TOGGLE = "AD_REDUCED_PRICE_TOGGLE";
const AD_SPECIAL_AD_FLAG = "AD_SPECIAL_AD_FLAG";
const AD_PRICE_CHANGES = "AD_PRICE_CHANGES";
const AD_STATUS_CHANGES = "AD_STATUS_CHANGES";
const AD_FREE_PROMOTION_ACTIVATE = "AD_FREE_PROMOTION_ACTIVATE";
const AD_FREE_PROMOTION_DEACTIVATE = "AD_FREE_PROMOTION_DEACTIVATE";
const AD_FREE_AUTO_PROMOTION_TOGGLE = "AD_FREE_AUTO_PROMOTION_TOGGLE";
const AD_MANUAL_PROMOTION_ACTIVATE = "AD_MANUAL_PROMOTION_ACTIVATE";
const AD_MANUAL_PROMOTION_DEACTIVATE = "AD_MANUAL_PROMOTION_DEACTIVATE";
const AD_MANUAL_AUTO_PROMOTION_ACTIVATE = "AD_MANUAL_AUTO_PROMOTION_ACTIVATE";
const AD_MANUAL_AUTO_PROMOTION_TOGGLE = "AD_MANUAL_AUTO_PROMOTION_TOGGLE";

const parsePriceChangesData = (priceChanges: PriceChange[]): PriceChangeParsed[] => {
  return priceChanges.map((priceChange) => {
    return {
      date: formatDate(priceChange.date, config.dateFormat),
      percentage: formatNumber(priceChange.percentage) + "%",
      priceFrom: formatNumber(priceChange.priceFrom) + config.currencySymbol,
      priceTo: formatNumber(priceChange.priceTo) + config.currencySymbol,
    }
  });
}

// The "Svi oglasi" tab is keyed by AdStatus.all (0), but the manage endpoint expects
// no status filter at all - a status of 0 would be treated as a real status to filter on.
// Returning null drops the param from the query string (see isAllowedValue).
const normalizeStatusFilter = (status: any) =>
  Number(status) === AdStatus.all ? null : status;

// actions
export const getAds = createAction(
  AD_GET,
  async (page, itemPerPage, searchCriteria) => {
    try {
      let ads;

      if (searchCriteria.status === AD_DUPLICATE_TAB_KEY) {
        // "duplicate" is a client-only tab key, so it must not be sent as a status filter
        const { status, ...duplicateCriteria } = searchCriteria;

        ads = await api.Ads.duplicate({
          ...duplicateCriteria,
          page,
          itemPerPage
        });
      } else {
        ads = await api.Ads.manage({
          ...searchCriteria,
          status: normalizeStatusFilter(searchCriteria.status),
          page,
          itemPerPage
        });
      }

      return { ads: ads.data, total: ads.count, page, itemPerPage };
    } catch (e) {
      return { ads: [], total: 0, page: 1 };
    }
  }
);
export const getDuplicateAds = createAction(
  AD_DUPLICATES_GET,
  async (adId, page, itemPerPage, searchCriteria) => {
    try {
      const duplicates = await api.Ads.duplicates(adId, {
        ...searchCriteria,
        page,
        itemPerPage
      });

      return duplicates;
    } catch (e) {
      return [];
    }
  }
);

export const flagAsDuplicate = createAction(
  AD_FLAG_AS_DUPLICATE,
  async (duplicateId, adId, isDuplicate, callback) => {
    try {
      await api.Ads.flagAsDuplicate(duplicateId, adId, isDuplicate);
      callback && callback();

      return null;
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

export const searchAds = createAction(AD_SEARCH, searchCriteria => ({
  searchCriteria
}));

export const changeAdStatus = createAction(
  AD_CHANGE_STATUS,
  async (id, status, ads) => {
    try {

      await api.Ads.changeStatus(id, status);

      const currentAd = ads.find(ad => ad.id === id);
      currentAd.status = status;

      return { ads: [...ads] };
    } catch (error) {
      return null;
    }
  }
);

// Blocking an ad goes through the dedicated /aditem/block endpoint, never
// through change-status, and must not touch the ad creator's user account.
export const blockAd = createAction(
  AD_BLOCK,
  async (id, ads) => {
    try {

      await api.Ads.block({ adId: id });

      const currentAd = ads.find(ad => ad.id === id);
      currentAd.status = AdStatus.blocked;

      return { ads: [...ads] };
    } catch (error) {
      return null;
    }
  }
);

export const getAdLinkData = createAction(AD_LINK_GET, async adId => {
  try {
    const { sharingConfiguration } = await api.Ads.details(adId);

    return sharingConfiguration;
  } catch (e) {
    return null;
  }
});

export const clearAdLinkData = createAction(AD_LINK_GET, () => {
  return null;
});

export const updateAdLink = createAction(
  AD_LINK_UPDATE,
  async (adId, data, callback) => {
    try {
      await api.Ads.updateShareLink(adId, data);

      callback && callback();
      return null;
    } catch (e) {
      callback && callback();

      return null;
    }
  }
);


// Get priceChanges log
export const getPriceChanges = createAction(
  AD_PRICE_CHANGES,
  async (
    queryParams,
    callback?: (priceChanges: PriceChangeListParsed | Boolean) => void
  ) => {

    const searchParams = {
      ...queryParams,
      sortOption: AdsSortOption.DateDesc,
      itemPerPage: config.maxAdPerPage / 4,
    }

    let priceChanges: PriceChangeList;
    try {

      priceChanges = await api.Ads.priceChanges(searchParams);

      if (!checkEmptyObject(priceChanges)) {
        callback && callback(false);
        return null;
      }

      const data = {
        data: parsePriceChangesData(priceChanges.data),
        count: priceChanges.count,
        page: priceChanges.currentPage,
        nextPage: priceChanges.nextPage,
        previousPage: priceChanges.previousPage,
      }

      callback && callback(data);
      return data;
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);


export const getStatusChanges = createAction(
  AD_STATUS_CHANGES,
  async (
    adId,
    queryParams = {},
    callback?
  ) => {
    try {

      const searchParams = {
        ...queryParams,
        sortOption: AdsSortOption.DateDesc,
        itemPerPage: config.maxAdPerPage / 4,
      }

      const statusChanges = await api.Ads.statusChanges(adId, searchParams);

      const data = {
        data: statusChanges.data.map(statusChange => ({
          ...statusChange,
          // Include the time so admins can tell overnight automatic jobs
          // apart from manual actions (task spec: "20.08.2026. 23:45").
          date: formatDate(statusChange.date, "DD.MM.YYYY. HH:mm"),
          oldStatus: getStatusString(statusChange.oldStatus),
          newStatus: getStatusString(statusChange.newStatus),
          // NOTE: `changedByEmail` used to be translated on the FE ("system"
          // → "Automatski (sistem)") and identity-transition rows (Aktivan
          // → Aktivan) got a synthetic "Oglas obnovljen / podignut u vrh"
          // label. Both concerns are now resolved backend-side — BE sends
          // human-readable actor labels ("Automatski (obnovljen)",
          // "Automatski (podignut na vrh)", …) directly in changedByEmail
          // so admins can read the exact reason without FE guesswork.
        })),
        count: statusChanges.count,
        page: statusChanges.currentPage,
        nextPage: statusChanges.nextPage,
        previousPage: statusChanges.previousPage,
      }

      callback && callback(data);
      return data;
    } catch (error) {
      callback && callback(false);
      return null;
    }
  }
);

// Clear priceChanges log
export const clearPriceChanges = createAction(AD_PRICE_CHANGES, () => {
  return null;
});

export const toogleAd18Old = createAction(
  AD_18_OLD_TOGGLE,
  async (shouldBe18Old, adId) => {
    try {
      if (shouldBe18Old) {
        await api.Ads.make18Old(adId);

        notification.show(
          "Ovaj oglas je označen kao 18+"
        );
      } else {
        await api.Ads.unmake18Old(adId);

        notification.show(
          "Uspješno neobilježeno"
        );
      }
    } catch (error) {
      // skip
    }
  }
);

export const toggleHideReducedPrice = createAction(
  AD_REDUCED_PRICE_TOGGLE,
  async (ad, callback) => {
    try {
      await api.Ads.reducedPrice({
        adId: ad.id,
        hideReducedPrice: !ad.hideReducedPrice
      });

      callback && callback(true);
    } catch (error) {
      callback && callback(error);

      console.error('error on toggle reduced price visibility')
    }
  }
);

export const activateFreePromotion = createAction(
  AD_FREE_PROMOTION_ACTIVATE,
  async (adId: string, duration: number, callback?: (success: boolean) => void) => {
    try {
      await api.Ads.activateFreePromotion(adId, duration);
      notification.show("Besplatna promocija uspješno aktivirana");
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const deactivateFreePromotion = createAction(
  AD_FREE_PROMOTION_DEACTIVATE,
  async (adId: string, callback?: (success: boolean) => void) => {
    try {
      await api.Ads.deactivateFreePromotion(adId);
      notification.show("Besplatna promocija uspješno deaktivirana");
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const toggleFreeAutoPromotion = createAction(
  AD_FREE_AUTO_PROMOTION_TOGGLE,
  async (adId: string, enable: boolean, callback?: (success: boolean) => void) => {
    try {
      if (enable) {
        await api.Ads.activateFreeAutoPromotion(adId);
        notification.show("Besplatna auto-promocija uspješno aktivirana");
      } else {
        await api.Ads.deactivateFreeAutoPromotion(adId);
        notification.show("Besplatna auto-promocija uspješno deaktivirana");
      }
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const activateManualPromotion = createAction(
  AD_MANUAL_PROMOTION_ACTIVATE,
  async (adId: string, duration: number, paymentMethod: number, callback?: (success: boolean) => void) => {
    try {
      await api.Ads.activateManualPromotion(adId, duration, paymentMethod);
      notification.show("Manualna promocija uspješno aktivirana");
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const activateManualAutoPromotion = createAction(
  AD_MANUAL_AUTO_PROMOTION_ACTIVATE,
  async (adId: string, callback?: (success: boolean) => void) => {
    try {
      await api.Ads.activateManualAutoPromotion(adId);
      notification.show("Manualna auto-promocija uspješno aktivirana");
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const deactivateManualPromotion = createAction(
  AD_MANUAL_PROMOTION_DEACTIVATE,
  async (adId: string, callback?: (success: boolean) => void) => {
    try {
      await api.Ads.deactivateManualPromotion(adId);
      notification.show("Plaćena promocija uspješno deaktivirana");
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

export const toggleManualAutoPromotion = createAction(
  AD_MANUAL_AUTO_PROMOTION_TOGGLE,
  async (adId: string, enable: boolean, callback?: (success: boolean) => void) => {
    try {
      if (enable) {
        await api.Ads.activateManualAutoPromotion(adId);
        notification.show("Plaćena auto-promocija uspješno aktivirana");
      } else {
        await api.Ads.deactivateManualAutoPromotion(adId);
        notification.show("Plaćena auto-promocija uspješno deaktivirana");
      }
      callback && callback(true);
    } catch (error) {
      callback && callback(false);
    }
  }
);

// selectors
export const selectAll = state => state.admin.ad;

// reducers
const updateState = (state, { payload }) => ({
  ...state,
  ...payload
});

const defaultState = {
  ads: [],
  total: 0,
  page: 1,
  itemPerPage: 50,
  searchCriteria: {
    title: null,
    id: null,
    module: null,
    category: null,
    status: null,
    creatorEmail: null,
    legalEntity: null
  },
  adData: null,
  adPriceChanges: null,
  adDuplicates: null,
  adStatusChanges: null,
};

export default handleActions(
  {
    [AD_GET]: updateState,
    [AD_SEARCH]: updateState,
    [AD_CHANGE_STATUS]: updateState,
    [AD_BLOCK]: updateState,
    [AD_LINK_GET]: (state, { payload }) => ({
      ...state,
      adData: { ...payload }
    }),
    [AD_PRICE_CHANGES]: (state, { payload }) => ({
      ...state,
      adPriceChanges: { ...payload }
    }),
    [AD_STATUS_CHANGES]: (state, { payload }) => ({
      ...state,
      adStatusChanges: { ...payload }
    }),
    [AD_DUPLICATES_GET]: (state, { payload }) => ({
      ...state,
      adDuplicates: [...payload]
    }),
  },
  defaultState
);



// WEBPACK FOOTER //
// ./src/routes/Admin/ManageAdPage/ad.ducks.ts